import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Package, FileText, DollarSign, Users, Settings,
  Truck, Building2, ChevronDown, ChevronRight, LogOut, Menu as MenuIcon,
  Bell, Search, Home, List
} from 'lucide-react';
import { logoutUser } from '../services/api';
import { menuService } from '../services/menuService';
import logoImg from '../assets/logo.png';
import useSessionHeartbeat from '../hooks/useSessionHeartbeat';

const Dashboard = ({ sessionData, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [menus, setMenus] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [menusLoaded, setMenusLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useSessionHeartbeat({ onLogout }); 
  // Verify session on mount
  useEffect(() => {
    const verifyDashboardSession = async () => {
      try {
        const response = await fetch('/api/verify-session.php', { credentials: 'include' });
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          console.warn('⚠️ Session verify returned non-JSON — skipping');
          return;
        }
        const data = await response.json();
        if (!data.success) {
          console.warn('Session invalid:', data.message);
          onLogout?.();
        }
      } catch (error) {
        console.error('❌ Session verification error:', error);
      }
    };
    verifyDashboardSession();
  }, [onLogout]);

  // Load menus on mount
  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      const userMenus = await menuService.getUserMenus();
      setMenus(userMenus);
      if (!userMenus || userMenus.length === 0) {
        console.warn('⚠️ No menus loaded from database');
      } else {
        console.log('✅ Loaded', userMenus.length, 'menu items from database');
      }
      const activePath = location.pathname.split('/').pop();
      userMenus.forEach(menu => {
        if (hasActiveChild(menu, activePath)) {
          setExpandedMenus(prev => new Set([...prev, menu.menu_id]));
        }
      });
    } catch (error) {
      console.error('❌ Error loading menus:', error);
      setMenus([]);
    } finally {
    setMenusLoaded(true);  
  }
  };
  const linkActive = menusLoaded
    ? 'bg-[#2563EB] text-white font-semibold'
    : '';
  const hasActiveChild = (menu, activePath) => {
    if (menu.route_path === activePath) return true;
    if (menu.children && menu.children.length > 0) {
      return menu.children.some(child => hasActiveChild(child, activePath));
    }
    return false;
  };

  const getIconComponent = (iconName) => {
    const icons = { LayoutDashboard, Package, FileText, DollarSign, Users, Settings, Truck, Building2, Home, List };
    return icons[iconName] || Package;
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) newSet.delete(menuId);
      else newSet.add(menuId);
      return newSet;
    });
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      onLogout();
    }
  };

  const isActive = (path) => {
    if (!menusLoaded) return false; 
    if (!path) return false;  
    return location.pathname === `/dashboard/${path}`;
  };

  // RECURSIVE MENU RENDERING
  const renderMenu = (menu, level = 0) => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus.has(menu.menu_id);
    const IconComponent = getIconComponent(menu.icon);
    const indentStyle = level > 0 ? { paddingLeft: `${level * 12}px` } : {};

    const linkBase =
      'flex items-center gap-3 px-4 py-3 rounded-lg font-medium no-underline transition-all duration-200 cursor-pointer';
    const linkIdle = 'text-white/90 hover:bg-[#334155] hover:text-white hover:translate-x-1';
     
    if (!hasChildren) {
      return (
        <li key={menu.menu_id} className="mx-2 my-1" style={indentStyle}>
          <Link
            to={`/dashboard/${menu.route_path || ''}`}
            className={`${linkBase} ${isActive(menu.route_path) ? linkActive : linkIdle}`}
          >
            <span className="flex items-center min-w-[24px]">
              <IconComponent size={20} />
            </span>
            {!collapsed && <span className="flex-1 text-[0.95rem]">{menu.menu_name}</span>}
          </Link>
        </li>
      );
    }

    return (
      <li key={menu.menu_id} className="mx-2 my-1" style={indentStyle}>
        <div
          className={`${linkBase} ${linkIdle}`}
          onClick={() => toggleMenu(menu.menu_id)}
        >
          <span className="flex items-center min-w-[24px]">
            <IconComponent size={20} />
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 text-[0.95rem]">{menu.menu_name}</span>
              <span className="ml-auto transition-transform duration-300">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </>
          )}
        </div>

        {!collapsed && isExpanded && (
          <ul className="list-none p-1 my-1 ml-3 bg-black/15 rounded-lg overflow-hidden border-l-2 border-white/15">
            {menu.children.map(child => renderMenu(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">

      {/* ── SIDEBAR ── */}
      <div
        className={`flex flex-col bg-[#1E293B] text-white transition-all duration-300 z-[100] relative flex-shrink-0
          ${collapsed ? 'w-20' : 'w-[280px]'}`}
        style={{ boxShadow: '4px 0 20px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-[14px] border-b border-white/10 bg-[#1E293B]">
          <div className="flex items-center gap-3 flex-1 overflow-hidden">
            <img
              src={logoImg}
              alt="LogIQ Global"
              className={`object-contain rounded-lg flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-16 h-16' : 'w-16 h-16'}`}
            />
            {!collapsed && (
              <div className="overflow-hidden">
                <h4 className="text-sm font-semibold truncate">
                  {sessionData?.user?.username || sessionData?.session?.username || 'User'}
                </h4>
                <p className="text-[0.78rem] text-white/85 truncate">
                  {sessionData?.company?.companyname || sessionData?.session?.companyname || 'Company'}
                </p>
                <small className="text-[0.72rem] text-white/70">
                  FY: {sessionData?.financial_year?.yearname || sessionData?.session?.yearname || 'N/A'}
                </small>
              </div>
            )}
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="bg-white/10 border-0 text-white p-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-white/20 hover:scale-105 flex-shrink-0"
          >
            <MenuIcon size={20} />
          </button>
        </div>

        {/* Nav */}
       <nav className={`flex-1 overflow-y-auto py-2.5 transition-opacity duration-300
          ${menusLoaded ? 'opacity-100' : 'opacity-0'}
          [&::-webkit-scrollbar]:w-2
          [&::-webkit-scrollbar-track]:bg-black/10
          [&::-webkit-scrollbar-track]:rounded
          [&::-webkit-scrollbar-thumb]:bg-white/30
          [&::-webkit-scrollbar-thumb]:rounded
          hover:[&::-webkit-scrollbar-thumb]:bg-white/50`}>
          <ul className="list-none p-0 m-0">
            {menus.length === 0 ? (
              <li className="mx-5 my-5 text-center text-white/60">
                {!collapsed ? (
                  <>
                    <p className="mb-2.5 text-xl">📋</p>
                    <p className="text-sm">No menus configured</p>
                    <p className="text-xs mt-1">Please add menu items to the database</p>
                  </>
                ) : (
                  <p className="text-xl">📋</p>
                )}
              </li>
            ) : (
              menus.map(menu => renderMenu(menu, 0))
            )}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/10 bg-black/20">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-3 w-full py-3 px-3 bg-[#DC2626]/15 border-2 border-[#DC2626]/30 text-white rounded-lg cursor-pointer text-[0.95rem] font-semibold transition-all duration-300 hover:bg-[#DC2626] hover:border-[#DC2626] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.4)]"
             style={{ color: '#ffffff' }}
          >
            <LogOut size={20} color="#ffffff" />  
  {!collapsed && <span style={{ color: '#ffffff' }}>Logout</span>}
          </button>
        </div>
      </div>
      {/* ── END SIDEBAR ── */}

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 flex flex-col overflow-hidden bg-[#F8FAFC]">
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F8FAFC]
          [&::-webkit-scrollbar]:w-2.5
          [&::-webkit-scrollbar-track]:bg-white/50
          [&::-webkit-scrollbar-track]:rounded-md
          [&::-webkit-scrollbar-thumb]:bg-white/80
          [&::-webkit-scrollbar-thumb]:rounded-md
          [&::-webkit-scrollbar-thumb]:border-2
          [&::-webkit-scrollbar-thumb]:border-[#F8FAFC]
          hover:[&::-webkit-scrollbar-thumb]:bg-white">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;